import React from 'react';
import type { LogocloudComponent as LogocloudComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getFontFamily } from '../utils/fontLoader';

interface LogocloudComponentProps {
  component: LogocloudComponentType;
}

const LOGOCLOUD_GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  // Single column on small phones; scale up progressively (no grid-cols-2 at default).
  4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  5: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
  6: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
};

// Logo height scales with both viewport width (vw) and logo count so that
// a few logos on a wide screen look substantial while many logos stay tidy.
// `userMax` is the user-configured baseline from the editor (default 48).
function buildLogoHeightClamp(count: number, userMax: number): string {
  // [preferredVw, ceilingMultiplier] per count bucket.
  // Ceiling is `userMax * multiplier`, capped at 200 for very large sliders.
  const [vw, ceilMult] =
    count <= 1
      ? [12, 3.5]
      : count <= 2
        ? [9, 2.8]
        : count <= 3
          ? [7, 2.2]
          : count <= 6
            ? [5, 1.5]
            : count <= 9
              ? [4, 1.2]
              : count <= 12
                ? [3.5, 1.0]
                : [3, 0.85];

  const ceiling = Math.min(200, Math.round(userMax * ceilMult));
  const floor = Math.min(ceiling, Math.max(32, Math.round(userMax * 0.75)));
  return `clamp(${floor}px, ${vw}vw, ${ceiling}px)`;
}

export const LogocloudComponent: React.FC<LogocloudComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const brandFonts = useBrandFonts();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Use stats colors as fallback when logocloud not in theme (e.g. older brand packages)
  const backgroundColor =
    (data as any).customColors?.background ||
    themeStyles.logocloud?.background ||
    themeStyles.stats?.background ||
    '#f9fafb';
  const titleColor =
    (data as any).customColors?.title ||
    themeStyles.logocloud?.titleColor ||
    themeStyles.stats?.valueColor ||
    '#111827';
  const subtitleColor =
    (data as any).customColors?.subtitle ||
    themeStyles.logocloud?.subtitleColor ||
    themeStyles.stats?.descriptionColor ||
    '#6b7280';

  const logos = data.logos || [];
  const layout = data.layout || 'row';
  const configuredColumns = Math.min(6, Math.max(2, data.columns ?? 4));
  const grayscale = data.grayscale ?? true;
  const baseMaxHeight = data.maxLogoHeight || 48;

  const count = logos.length;
  // In grid mode, don't render empty trailing cells when there are fewer
  // logos than configured columns.
  const effectiveColumns = Math.min(configuredColumns, Math.max(1, count));
  const logoHeight = buildLogoHeightClamp(count, baseMaxHeight);

  const logoStyle: React.CSSProperties = {
    height: logoHeight,
    width: 'auto',
    objectFit: 'contain',
    ...(grayscale ? { filter: 'grayscale(100%)', opacity: 0.6 } : {}),
    transition: 'filter 0.3s ease, opacity 0.3s ease',
  };

  const renderLogo = (logo: (typeof logos)[number], index: number) => {
    const logoSrc = (logo as any).cloudfrontUrl || logo.imageUrl;
    const logoContent = logoSrc ? (
      <img
        src={logoSrc}
        alt={logo.name || `Partner logo ${index + 1}`}
        style={logoStyle}
        className="hover:filter-none hover:opacity-100"
        onMouseEnter={e => {
          if (grayscale) {
            e.currentTarget.style.filter = 'grayscale(0%)';
            e.currentTarget.style.opacity = '1';
          }
        }}
        onMouseLeave={e => {
          if (grayscale) {
            e.currentTarget.style.filter = 'grayscale(100%)';
            e.currentTarget.style.opacity = '0.6';
          }
        }}
      />
    ) : (
      <div
        className="flex items-center justify-center bg-gray-200 rounded px-6"
        style={{ height: logoHeight, minWidth: '120px' }}
      >
        <span className="text-sm text-gray-400" style={bodyFontStyle}>
          {logo.name || `Logo ${index + 1}`}
        </span>
      </div>
    );

    // Show company name below the logo when we have both an image and a name
    const companyNameLabel = logo.name && logoSrc && (
      <span
        className="text-sm font-medium mt-2 block text-center"
        style={{ color: subtitleColor, ...bodyFontStyle }}
      >
        {logo.name}
      </span>
    );

    const content = (
      <div className="flex flex-col items-center justify-center">
        {logoContent}
        {companyNameLabel}
      </div>
    );

    if (logo.linkUrl) {
      return (
        <a
          key={index}
          href={logo.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center"
        >
          {content}
        </a>
      );
    }

    return (
      <div key={index} className="flex items-center justify-center">
        {content}
      </div>
    );
  };

  const gridClasses =
    layout === 'grid'
      ? `grid ${LOGOCLOUD_GRID_COLS[effectiveColumns] || LOGOCLOUD_GRID_COLS[4]} gap-8 items-center justify-items-center [&>*]:min-w-0`
      : 'flex flex-wrap items-center justify-center gap-x-12 gap-y-8';

  return (
    <section className="py-16 md:py-20" style={{ backgroundColor }}>
      <div className="max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8">
        {(data.title || data.subtitle) && (
          <div className="text-center mb-12">
            {data.title && (
              <h2
                className="text-xl md:text-2xl font-semibold mb-2"
                style={{ color: titleColor, ...headingFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
              />
            )}
            {data.subtitle && (
              <p
                className="text-base opacity-80"
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
              />
            )}
          </div>
        )}

        <div className={gridClasses}>{logos.map((logo, index) => renderLogo(logo, index))}</div>
      </div>
    </section>
  );
};
